import type { ReactNode } from "react";

import ryanIcon from "../assets/ryan-icon.png";

const H2_CLASS =
  "font-companion tracking-[-0.01em] mt-14 mb-5 text-[clamp(26px,3vw,34px)] font-bold leading-[1.2] tracking-[-0.02em] text-primary";

const P_CLASS =
  "mb-[22px] font-body text-[19px] leading-[1.7] text-grindigo-800 [text-wrap:pretty]";

function PullQuote({ children }: { children: ReactNode }) {
  return (
    <blockquote className="my-10 rounded-r-2xl border-l-4 border-indigo-500 bg-[linear-gradient(135deg,#EEF2FF_0%,#F5F3FF_100%)] px-8 py-7 font-companion text-[22px] font-medium italic leading-[1.45] tracking-[-0.01em] text-grindigo-800">
      {children}
    </blockquote>
  );
}

export function BlogBody() {
  return (
    <article className="px-[clamp(24px,5vw,80px)] pb-24">
      <div className="mx-auto max-w-[720px]">
        {/* Awe and Sadness */}
        <h2 className={H2_CLASS}>Awe and Sadness</h2>

        <p className={P_CLASS}>
          Every time I visit a veterinary clinic, I feel two competing emotions:
          awe and sadness.
        </p>

        <p className={P_CLASS}>
          The awe comes from watching the people of veterinary medicine and what
          they do day-to-day. I once watched a foreign body surgery on a
          Chihuahua. Towards the end, the surgery went sideways as the
          patient&apos;s blood pressure started dropping fast. The surgeon acted
          with precision and calm, the team was fast and supportive, and the
          patient survived.
        </p>

        <PullQuote>
          &ldquo;Wow — well that was dicey… Hey by the way, that thing you were
          telling me about earlier… what if we…&rdquo;, says the surgeon a few
          minutes after he&apos;s scrubbed out.
        </PullQuote>

        <p className={P_CLASS}>
          My mind was still processing what just happened. The surgeon, however,
          had already moved on. This was the way vet professionals operate. It
          is insanely impressive.
        </p>

        <p className={P_CLASS}>
          My sadness comes from watching what happens next — the in-between care
          stuff. I watched the surgeon walk to the other room and pull up to his
          computer. Click. Click. Click. Double click. Furious typing. Click
          click click. A few minutes go by. &ldquo;Fluffy is ready for you
          now&rdquo; a team member reports. Rinse, repeat, all day long.
        </p>

        {/* Why are the software tools so bad */}
        <h2 className={H2_CLASS}>Why are the software tools so bad?</h2>

        <p className={P_CLASS}>
          In my view, &ldquo;bad veterinary software&rdquo; is not just the 30
          year old PIMS you&apos;re using. You&apos;d have similar experiences
          on a modern PIMS. Sure, some software is easier and faster to use than
          others. Regardless of tool vintage, you&apos;re still on the computer
          for cumulative hours each day. You have to use the software, which
          takes real time and attention to operate. This causes workflow
          tension: your eyes and mind are focused on operating software, but
          your job is rooted in the physical world.
        </p>

        <p className={P_CLASS}>
          This idea echoes the core discovery we made at Scribenote in December
          2019. At the time, dictation — i.e., slowly speaking into a microphone
          — was popular but only a small percentage of vets were dictating.
          &ldquo;Dictating is not as fast for me as just typing&rdquo; said my
          big sister Dr. Katie, and so did many other veterinarians like her.
          She was always getting home late after catching up on medical records.
          When I followed her to the clinic to see if I could help as an
          engineer, I had my first big idea: that an AI listening to the
          appointment and writing a note on Katie&apos;s behalf was inevitable.
          In a few years — I bet ten — it would not make sense for a
          veterinarian to use dictation or canned templates over an AI Scribe.
        </p>

        <p className={P_CLASS}>
          So far we are on track for this prediction to be true.
        </p>

        <p className={P_CLASS}>
          AI Scribing took off so quickly in vet med because it was so easy!
          Just click a button, then review your SOAP. Increasingly smart AI
          eventually learned how to write your notes, then how to write notes
          your way. Sometimes you felt it read your mind.
        </p>

        {/* Bad Software */}
        <h2 className={H2_CLASS}>Bad Software</h2>

        <p className={P_CLASS}>
          I say this without condemnation — I build vet software for a living!
          While we designed Scribenote to be delightful, easy-to-use software,
          every day we face reality in customer support inquiries. Our users
          don&apos;t always find our software obvious. They don&apos;t always
          find it can do anything they need, because it can&apos;t. Nothing
          could. What our customers needed was for their computer to take on a
          10-minute long set of digital tasks through natural language, and have
          it execute correctly through to completion and review. A digital
          brain.
        </p>

        <PullQuote>
          We have been working on a digital brain for the vet clinic for a long
          time. We call it Companion.
        </PullQuote>

        <p className={P_CLASS}>
          We released an early version of Companion as a free beta last year. It
          could summarize your histories, edit your records and templates, and
          do veterinary research on the internet for you. This is helpful, but
          not enough. A sufficiently good brain can handle anything reasonably
          within the bounds of veterinary medicine on your behalf. We believe we
          are close to building that brain.
        </p>

        <p className={P_CLASS}>
          We think Companion will transform veterinary medicine even quicker
          than our first product, Scribenote. Companion is aware and
          intelligent. It can learn YOUR preferences and remember them.
          Companions cost treats — i.e. usage credits — that help you pay for
          what you use in intelligence. Teams will be able to use it to attack
          their biggest bottlenecks and make care run much smoother. We&apos;ll
          start with tasks within Scribenote, but we think soon we&apos;ll be
          able to take on any task on your computer.
        </p>

        <p className={P_CLASS}>
          In short order — I bet less than 2 years — we will not only free
          veterinary staff from mundane admin tasks, but also help them spend
          more time with pets and pet parents. That&apos;s the idea behind the
          clinic&apos;s digital brain.
        </p>

        <p className={P_CLASS}>
          Our mission is to decrease the cost of veterinary care while helping
          veterinary professionals spend more time doing what they love.
          Companion is our love letter to that mission.
        </p>

        <p className={`${P_CLASS} font-semibold text-primary`}>
          Companion is here. Get started for free at{" "}
          <a
            href="https://app.scribenote.com/dashboard?companion"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline underline-offset-4"
          >
            app.scribenote.com
          </a>{" "}
          — your first 500 Treats are on us.
        </p>

        {/* Signature */}
        <div className="mt-14 flex items-center gap-4 border-t border-grindigo-100 pt-8">
          <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full">
            <img
              src={ryanIcon}
              alt="Ryan Gallagher"
              className="block h-full w-full object-cover"
            />
          </div>
          <div>
            <div className="font-companion tracking-[-0.01em] text-[17px] font-semibold tracking-[-0.01em] text-primary">
              Ryan Gallagher
            </div>
            <div className="text-sm text-secondary">Co-founder, Scribenote</div>
          </div>
        </div>
      </div>
    </article>
  );
}
